import React, { useEffect, useState, useCallback } from 'react'
import { App, Button, Input, Switch } from 'antd'
import { supabaseClient } from '../lib/supabaseClient'
import { brand } from '../theme/brand'
import { ListPage, SectionCard } from '../theme/listkit'
import { ShieldAlert, ShieldCheck, RefreshCw } from '../components/icons'
import { UrgentNoticeCard } from '../components/UrgentNoticeCard'

/**
 * App availability & access — the two switches that decide who can use the app
 * at all. Both live on the same singleton `app_config` row (id = true), which the
 * mobile app reads once at launch and then watches over Realtime, so either one
 * lands on every REACHABLE device within seconds — no redeploy, no app-store
 * release.
 *
 * "Reachable" is load-bearing in the copy on this page, and is not hedging.
 * mobile/src/lib/killSwitch.tsx wraps that read in try/catch and, on any
 * transport failure, leaves the value at the fallback bundled into the build
 * (mobile/src/lib/appDefaults.ts — deliberately NOT app.json, whose keys feed
 * the fingerprint runtimeVersion and would cut OTAs off from installed apps) —
 * which for guest browsing is ON. That is deliberate (a store-review
 * requirement must not vanish because the backend blipped), but it means a
 * visitor who is offline, behind a captive portal, or riding out a Supabase
 * outage KEEPS browsing as a guest until their phone can ask again. So this
 * page must never promise an admin that turning guest browsing off reaches
 * "every" guest "right away" — it reaches each one at their next successful
 * check-in. Anything stronger is a promise the client does not keep.
 *
 * They are deliberately NOT the same kind of control, and the page is laid out to
 * make that obvious at a glance:
 *
 *  - KILL SWITCH (`kill_switch`) is an INCIDENT lever. ON takes the ENTIRE app
 *    offline for EVERY user behind an "under construction" screen. It owns the
 *    big tinted status card, the red treatment, and a confirmation in BOTH
 *    directions, because both directions are incident-grade decisions.
 *  - GUEST BROWSING (`guest_mode_enabled`) is an ordinary PRODUCT setting: may a
 *    visitor look around without an account? It gets a plain white card, no red,
 *    and a confirmation only on the disruptive direction (OFF). An admin who
 *    reaches for one of these must never mistake it for the other, which is why
 *    red is reserved for the kill switch alone.
 *
 * Data access:
 *  - READ  via supabaseClient.from('app_config').select('*').eq('id', true) —
 *    `select('*')` so a newly-added column needs no query change here.
 *  - WRITE via supabaseClient.from('app_config').update(...).eq('id', true). RLS
 *    permits UPDATE only for admins; the admin app is already signed in as one.
 *    A narrow cast sidesteps the not-yet-generated table type.
 *
 * Single-row contract: the table always holds exactly one row whose boolean
 * primary key id is `true`. We query and update by id = true.
 */

const APP_CONFIG_RESOURCE = 'app_config'

interface AppConfig {
  id: boolean
  kill_switch: boolean
  guest_mode_enabled: boolean
  message: string
  updated_at: string | null
  updated_by: string | null
  // The urgent notice (see components/UrgentNoticeCard.tsx). Optional because an
  // admin build can be newer than the database it is pointed at — the columns
  // arrive in 20260917120000_app_config_announcement.sql, and until then the read
  // simply returns a row without them rather than failing.
  announcement?: string | null
  announcement_active?: boolean | null
  announcement_level?: string | null
  announcement_audience?: unknown
  announcement_published_at?: string | null
}

const DEFAULT_MESSAGE =
  'We are temporarily offline for maintenance. Please check back soon.'

// The EXACT fallback the mobile app shows when app_config.message is empty —
// mirrored verbatim from mobile/src/lib/killSwitch.tsx so the iOS preview below
// is a true 1:1 of what members actually see (this differs from the textarea
// placeholder above, which is only the admin-side hint).
const MOBILE_DEFAULT_MESSAGE =
  "We're making some improvements and will be back shortly. Please check back later."

/**
 * The reason a write failed, in words an admin can act on.
 *
 * This exists because of a supabase-js detail that is easy to get wrong in
 * exactly the place it hurts most. postgrest-js only wraps a failed response
 * body in `new PostgrestError(...)` — a real Error subclass — when the query was
 * built with `.throwOnError()`. The writes on this page are not: they destructure
 * `{ error }` and rethrow it, so what arrives here is a PLAIN OBJECT parsed
 * straight out of the response body. `e instanceof Error` is therefore FALSE for
 * it, and the usual `String(e)` fallback renders it as the literal
 * "[object Object]". An admin whose UPDATE was refused by RLS was being shown
 * "Failed to update guest browsing: [object Object]" — no message, no code, no
 * hint, nothing to act on, on a page whose whole job is incident-grade levers.
 *
 * So read the PostgREST shape explicitly ({ message, details, hint, code }) and
 * keep the parts that say WHICH policy or constraint refused the write. Reading
 * `message` as a property rather than branching on `instanceof` covers both
 * cases with one path: a real Error (including a genuine PostgrestError, whose
 * details/hint/code would be lost by an `instanceof` branch that only took
 * `.message`) and the plain body object. The last resort is readable prose,
 * never a stringified object — if we cannot say why, we at least say plainly
 * that nothing was saved.
 */
export function describeWriteError(e: unknown): string {
  if (typeof e === 'string' && e.trim()) return e.trim()
  if (e != null && typeof e === 'object') {
    const r = e as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
    const parts = [r.message, r.details, r.hint]
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
      .map((p) => p.trim())
    const code = typeof r.code === 'string' && r.code.trim() ? ` (${r.code.trim()})` : ''
    if (parts.length) return `${parts.join(' — ')}${code}`
    if (code) return `the server refused the change${code}`
  }
  return 'the server gave no reason'
}

/** "Last changed 3 minutes ago" — compact relative time, falling back gracefully. */
function relativeTime(iso: string | null): string {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'unknown'
  const diffMs = Date.now() - then
  const sec = Math.round(diffMs / 1000)
  if (sec < 45) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.round(hr / 24)
  return `${day} day${day === 1 ? '' : 's'} ago`
}

export function KillSwitchPage() {
  const { message, modal } = App.useApp()

  // The single app_config row (id = true). Read via supabaseClient directly —
  // the boolean PK + singleton don't fit Refine's id (BaseKey = string|number).
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)

  const loadConfig = useCallback(async () => {
    setIsFetching(true)
    const { data, error } = await (supabaseClient.from(APP_CONFIG_RESOURCE) as any)
      .select('*')
      .eq('id', true)
      .maybeSingle()
    if (!error) setConfig((data as AppConfig) ?? null)
    setIsLoading(false)
    setIsFetching(false)
  }, [])

  const killSwitchOn = config?.kill_switch ?? false

  // Guest browsing defaults to ON at EVERY layer — the column default, the app's
  // bundled fallback, and here. So while the row is still loading, and if the read
  // failed outright (config stays null), this reads ON rather than flashing
  // "sign-in required" and scaring an admin into thinking access was revoked.
  const guestModeOn = config?.guest_mode_enabled ?? true

  // Local draft of the public message; synced from the row when it (re)loads.
  const [draftMessage, setDraftMessage] = useState('')
  const [toggling, setToggling] = useState(false)
  // Separate from `toggling`: the two switches write independent columns, and a
  // shared busy flag would spin both spinners for one admin's single click.
  const [savingGuestMode, setSavingGuestMode] = useState(false)
  // A toast is easy to miss and gone in seconds. A failed write leaves the switch
  // showing the OLD server value, which looks like nothing happened — so the
  // reason it didn't stick stays pinned to the card until the next attempt.
  const [guestModeError, setGuestModeError] = useState<string | null>(null)
  const [savingMessage, setSavingMessage] = useState(false)

  useEffect(() => {
    if (config) setDraftMessage(config.message ?? '')
  }, [config])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  const refetch = loadConfig

  /** Persist arbitrary columns to the single app_config row (id = true). */
  async function patchConfig(values: Partial<AppConfig>): Promise<void> {
    const { error } = await (supabaseClient.from(APP_CONFIG_RESOURCE) as any)
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', true)
    if (error) throw error
  }

  async function applyToggle(next: boolean) {
    setToggling(true)
    try {
      await patchConfig({ kill_switch: next })
      await refetch()
      if (next) {
        message.success('App is now LOCKED DOWN — all users see the under-construction screen.')
      } else {
        message.success('App is back ONLINE for all users.')
      }
    } catch (e) {
      const msg = describeWriteError(e)
      message.error(`Failed to update kill switch: ${msg}`)
    } finally {
      setToggling(false)
    }
  }

  /** A switch flip always routes through an explicit confirmation modal. */
  function requestToggle(next: boolean) {
    if (next) {
      modal.confirm({
        title: 'Take the ENTIRE app offline?',
        okText: 'Yes, lock down the app',
        okType: 'danger',
        cancelText: 'Cancel',
        width: 520,
        content: (
          <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
            <p style={{ marginTop: 4 }}>
              This <strong>immediately takes the entire app offline for ALL users</strong> and
              shows an under-construction screen on every device.
            </p>
            <p style={{ marginBottom: 0, color: '#a4452f', fontWeight: 600 }}>
              Use this only during a security or integrity incident.
            </p>
          </div>
        ),
        onOk: () => applyToggle(true),
      })
    } else {
      modal.confirm({
        title: 'Bring the app back online for all users?',
        okText: 'Yes, go back online',
        okType: 'primary',
        cancelText: 'Cancel',
        width: 480,
        content: (
          <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
            Members will regain full access immediately. Confirm only once you are sure the
            incident is resolved.
          </div>
        ),
        onOk: () => applyToggle(false),
      })
    }
  }

  async function applyGuestMode(next: boolean) {
    setSavingGuestMode(true)
    setGuestModeError(null)
    try {
      await patchConfig({ guest_mode_enabled: next })
      await refetch()
      if (next) {
        message.success('Visitors can now browse the app without an account.')
      } else {
        message.success(
          'Guest browsing is OFF — an account is now required. Guests are returned to the sign-in screen as their phones check in, normally within seconds.',
        )
      }
    } catch (e) {
      const msg = describeWriteError(e)
      setGuestModeError(msg)
      message.error(`Failed to update guest browsing: ${msg}`)
    } finally {
      setSavingGuestMode(false)
    }
  }

  /**
   * Only the OFF direction confirms. Turning guest browsing ON simply widens
   * access and is invisible to anyone already signed in; turning it OFF ejects
   * every visitor mid-session, which is disruptive enough to be worth a beat —
   * but it is still a reversible product setting, so the modal stays `primary`.
   * Red/`danger` belongs to the kill switch alone.
   */
  function requestGuestModeToggle(next: boolean) {
    if (next) {
      void applyGuestMode(true)
      return
    }
    modal.confirm({
      title: 'Require an account to use the app?',
      okText: 'Yes, require sign-in',
      okType: 'primary',
      cancelText: 'Cancel',
      width: 520,
      content: (
        <div style={{ fontFamily: brand.fSans, fontSize: 13, lineHeight: 1.6, color: brand.ink }}>
          <p style={{ marginTop: 4 }}>
            Visitors will no longer be able to look around without an account. The{' '}
            <strong>&ldquo;Browse without an account&rdquo;</strong> button disappears from the
            sign-in screen, and anyone browsing as a guest is returned to sign-in as soon as their
            phone reaches our servers — normally within seconds. A phone that is offline keeps
            browsing until it reconnects.
          </p>
          <p style={{ marginBottom: 0 }}>
            Members who are already signed in are not affected. You can turn this back on at any
            time.
          </p>
        </div>
      ),
      onOk: () => applyGuestMode(false),
    })
  }

  async function saveMessage() {
    setSavingMessage(true)
    try {
      await patchConfig({ message: draftMessage.trim() })
      await refetch()
      message.success('Under-construction message saved.')
    } catch (e) {
      // The same plain-object error reaches this catch — all three writes on this
      // page go through patchConfig, so all three shared the "[object Object]" bug.
      const msg = describeWriteError(e)
      message.error(`Failed to save message: ${msg}`)
    } finally {
      setSavingMessage(false)
    }
  }

  const messageDirty =
    config != null && draftMessage.trim() !== (config.message ?? '').trim()

  // ----- Prominent status card colors -----
  const live = !killSwitchOn
  const statusBg = live ? 'rgba(31,122,77,.07)' : 'rgba(164,69,47,.08)'
  const statusBorder = live ? 'rgba(31,122,77,.30)' : 'rgba(164,69,47,.35)'
  const statusFg = live ? '#1f7a4d' : '#a4452f'
  const StatusIcon = live ? ShieldCheck : ShieldAlert

  return (
    <ListPage
      intro={{
        eyebrow: 'System · App Controls',
        // The tab/header already says "Kill Switch & Notices". An in-page title is the
        // sanctioned way (theme/listkit IntroProps) to widen the heading now that
        // the page carries a second, non-incident control, without renaming the
        // nav entry — an incident lever has to keep the name people search for
        // when something is on fire.
        title: 'App availability & access',
        description:
          'Three controls, all reaching every connected device within seconds. The KILL SWITCH is an incident lever: when ON, the app locks down to an "under construction" screen for EVERY user. The URGENT NOTICE puts a message across the top of every screen without taking anything away. GUEST BROWSING is an everyday setting: whether visitors can look around without an account. To require a minimum app version instead — per platform, on release day — use App Updates.',
      }}
    >
      {/* Prominent status card */}
      <div
        data-testid="kill-switch-status"
        style={{
          background: statusBg,
          border: `1.5px solid ${statusBorder}`,
          borderRadius: 16,
          padding: '24px 26px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 280 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: live ? 'rgba(31,122,77,.12)' : 'rgba(164,69,47,.14)',
              border: `1px solid ${statusBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <StatusIcon size={30} color={statusFg} />
          </div>
          <div>
            <div
              style={{
                fontFamily: brand.fDisplay,
                fontWeight: 800,
                fontSize: 24,
                color: statusFg,
                lineHeight: 1.1,
                letterSpacing: '.01em',
              }}
            >
              {isLoading ? 'Loading…' : live ? 'APP IS LIVE' : 'APP IS LOCKED DOWN'}
            </div>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 500,
                fontSize: 12.5,
                color: brand.sub,
                marginTop: 5,
                maxWidth: 460,
                lineHeight: 1.5,
              }}
            >
              {isLoading
                ? 'Reading the current configuration…'
                : live
                ? 'All members have full access to the app.'
                : 'An under-construction screen is shown to ALL users right now.'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <span
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '.05em',
              textTransform: 'uppercase',
              color: brand.sub,
            }}
          >
            {killSwitchOn ? 'Locked' : 'Live'}
          </span>
          <Switch
            data-testid="kill-switch-toggle"
            checked={killSwitchOn}
            loading={toggling}
            disabled={isLoading}
            onChange={(checked) => requestToggle(checked)}
            style={killSwitchOn ? { background: '#a4452f' } : undefined}
          />
        </div>
      </div>

      {/* Urgent notice — the middle lever: louder than a product setting, far
          short of taking the app offline. Gold, never red. */}
      <UrgentNoticeCard
        config={config}
        isLoading={isLoading}
        patchConfig={patchConfig}
        refetch={refetch}
        describeWriteError={describeWriteError}
      />

      {/* Guest browsing — an ordinary product setting, deliberately styled as a
          plain card with no red anywhere, so it cannot be mistaken for the
          incident lever above it. */}
      <SectionCard
        title="Guest browsing"
        sub="Whether someone can open the app and look around — events, news, the About page — without creating an account or signing in."
        style={{ maxWidth: 720, marginBottom: 18 }}
      >
        <div
          data-testid="guest-mode-card"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}
        >
          <div style={{ flex: 1, minWidth: 300 }}>
            <div
              data-testid="guest-mode-state"
              style={{
                fontFamily: brand.fSans,
                fontWeight: 700,
                fontSize: 14,
                color: brand.ink,
                marginBottom: 6,
              }}
            >
              {isLoading
                ? 'Loading…'
                : guestModeOn
                ? 'Visitors can browse without an account'
                : 'An account is required to use the app'}
            </div>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 500,
                fontSize: 12.5,
                color: brand.sub,
                lineHeight: 1.6,
              }}
            >
              {guestModeOn ? (
                <>
                  The sign-in screen offers a <strong>&ldquo;Browse without an account&rdquo;</strong>{' '}
                  button. Guests get a read-only tour — anything that needs an identity (RSVP,
                  posting, messaging, giving) still asks them to sign in.
                </>
              ) : (
                <>
                  The <strong>&ldquo;Browse without an account&rdquo;</strong> button is hidden.
                  Everyone lands on the normal sign-in screen.
                </>
              )}
            </div>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 500,
                fontSize: 12,
                color: brand.sub,
                lineHeight: 1.6,
                marginTop: 10,
              }}
            >
              Turning this <strong>off</strong> takes effect as soon as a phone reaches our
              servers — normally within seconds, and with no app update needed. Someone browsing
              as a guest right then is returned to the sign-in screen. A phone that is offline or
              on a Wi-Fi network that hasn&rsquo;t let it through yet keeps browsing until it
              reconnects, and is returned to sign-in at that point. Members who are already signed
              in are unaffected. This setting is <strong>on by default</strong>.
            </div>
            {guestModeError && (
              <div
                data-testid="guest-mode-error"
                style={{
                  fontFamily: brand.fSans,
                  fontWeight: 600,
                  fontSize: 12,
                  color: '#a4452f',
                  lineHeight: 1.5,
                  marginTop: 10,
                }}
              >
                Couldn&rsquo;t save this change: {guestModeError}. The switch still shows the
                setting that is actually live — try again.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <span
              style={{
                fontFamily: brand.fSans,
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                color: brand.sub,
              }}
            >
              {guestModeOn ? 'On' : 'Off'}
            </span>
            <Switch
              data-testid="guest-mode-toggle"
              checked={guestModeOn}
              loading={savingGuestMode}
              disabled={isLoading}
              onChange={(checked) => requestGuestModeToggle(checked)}
            />
          </div>
        </div>
      </SectionCard>

      {/* Audit / metadata */}
      <SectionCard
        title="Last change"
        sub="Stamped by any edit on this page — the kill switch, the urgent notice, guest browsing, or the under-construction message — because they all live on the same single row."
        style={{ marginBottom: 18 }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
          <div>
            <div style={auditLabel}>When</div>
            <div style={auditValue}>
              {config?.updated_at
                ? `${relativeTime(config.updated_at)} · ${new Date(
                    config.updated_at,
                  ).toLocaleString()}`
                : '—'}
            </div>
          </div>
          <div>
            <div style={auditLabel}>Changed by</div>
            <div style={{ ...auditValue, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
              {config?.updated_by ?? '—'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            <Button
              size="small"
              icon={
                <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                  <RefreshCw size={14} color={brand.jaguar} />
                </span>
              }
              loading={isFetching && !isLoading}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* Public message editor */}
      <SectionCard
        title="Under-construction message"
        sub="Shown to every user while the app is locked down. Keep it short and reassuring."
        style={{ maxWidth: 720 }}
      >
        <Input.TextArea
          data-testid="kill-switch-message"
          rows={4}
          value={draftMessage}
          placeholder={DEFAULT_MESSAGE}
          maxLength={500}
          onChange={(e) => setDraftMessage(e.target.value)}
          disabled={isLoading || savingMessage}
          style={{ fontFamily: brand.fSans }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <Button
            type="primary"
            loading={savingMessage}
            disabled={isLoading || !messageDirty}
            onClick={saveMessage}
          >
            Save message
          </Button>
          {messageDirty && (
            <span style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
              Unsaved changes
            </span>
          )}
        </div>
      </SectionCard>

      {/* Live iOS preview — a 1:1 mirror of the app's under-construction screen */}
      <SectionCard
        title="Live preview · iOS"
        sub="Exactly what every member sees on their device while the app is locked down — updates live as you edit the message above."
        style={{ maxWidth: 720 }}
      >
        <KillSwitchPreview message={draftMessage.trim() || MOBILE_DEFAULT_MESSAGE} />
      </SectionCard>
    </ListPage>
  )
}

/**
 * KillSwitchPreview — an iOS-framed, 1:1 mirror of the mobile under-construction
 * screen (mobile/src/app/screens/UnderConstructionScreen.tsx) — the ONLY thing the
 * app renders while the kill switch is ON. The crest, "MUAA ALUMNI" eyebrow,
 * "Under Construction" title, gold rule, and "Please check back later." footer are
 * the screen's HARDCODED copy; `message` is the live admin text. Colors + fonts are
 * the shared brand tokens (navy #06122B, gold #C49731 / #E6C14E; Cinzel display,
 * Cormorant Garamond italic, Montserrat — all loaded in index.html).
 */
function KillSwitchPreview({ message }: { message: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 6px' }}>
      <div
        style={{
          width: 290,
          height: 602,
          borderRadius: 48,
          background: '#0b0e16',
          padding: 11,
          boxShadow: '0 26px 60px rgba(6,18,43,.42), inset 0 0 0 1.5px rgba(255,255,255,.07)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: 38,
            overflow: 'hidden',
            background: '#06122B',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Dynamic Island */}
          <div
            style={{
              position: 'absolute',
              top: 11,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 88,
              height: 25,
              borderRadius: 13,
              background: '#000',
              zIndex: 2,
            }}
          />
          {/* Status bar */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 26px 0',
              color: '#fff',
              fontFamily: brand.fSans,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <span>9:41</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width="17" height="11" viewBox="0 0 17 11" fill="#fff" aria-hidden>
                <rect x="0" y="7" width="3" height="4" rx="1" />
                <rect x="4.5" y="5" width="3" height="6" rx="1" />
                <rect x="9" y="2.5" width="3" height="8.5" rx="1" />
                <rect x="13.5" y="0" width="3" height="11" rx="1" />
              </svg>
              <svg width="16" height="12" viewBox="0 0 16 12" fill="#fff" aria-hidden>
                <path d="M8 2.5c2.6 0 5 1 6.8 2.7l-1.4 1.5A7.6 7.6 0 0 0 8 4.6 7.6 7.6 0 0 0 2.6 6.7L1.2 5.2A9.7 9.7 0 0 1 8 2.5Zm0 3.4c1.5 0 2.9.6 4 1.6l-1.5 1.5A3.5 3.5 0 0 0 8 8.1c-.9 0-1.8.3-2.5.9L4 7.5A5.7 5.7 0 0 1 8 5.9Zm0 3.3c.6 0 1.2.2 1.6.7L8 11.2 6.4 9.6c.4-.5 1-.7 1.6-.7Z" />
              </svg>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span
                  style={{
                    width: 22,
                    height: 11,
                    border: '1px solid rgba(255,255,255,.5)',
                    borderRadius: 3,
                    padding: 1.5,
                    display: 'inline-flex',
                  }}
                >
                  <span style={{ flex: 1, background: '#fff', borderRadius: 1.5 }} />
                </span>
                <span
                  style={{
                    width: 1.5,
                    height: 4,
                    background: 'rgba(255,255,255,.5)',
                    borderRadius: 1,
                    marginLeft: 1,
                  }}
                />
              </span>
            </span>
          </div>
          {/* Under-construction content — 1:1 with UnderConstructionScreen */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 30px 24px',
              gap: 11,
            }}
          >
            <img
              src="/crest.png"
              alt="MUAA seal"
              style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
            />
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '.18em',
                textTransform: 'uppercase',
                color: '#E6C14E',
                marginTop: 13,
              }}
            >
              MUAA Alumni
            </div>
            <div
              style={{
                fontFamily: brand.fDisplay,
                fontWeight: 700,
                fontSize: 23,
                lineHeight: 1.23,
                color: '#fff',
                textAlign: 'center',
              }}
            >
              Under Construction
            </div>
            <div style={{ width: 56, height: 2, borderRadius: 1, background: '#C49731', margin: '2px 0' }} />
            <div
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 15.5,
                lineHeight: 1.47,
                color: 'rgba(241,245,249,.82)',
                textAlign: 'center',
              }}
            >
              {message}
            </div>
            <div
              style={{
                fontFamily: brand.fSans,
                fontWeight: 500,
                fontSize: 12,
                color: 'rgba(241,245,249,.5)',
                textAlign: 'center',
                marginTop: 5,
              }}
            >
              Please check back later.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const auditLabel: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 10.5,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: brand.sub,
  marginBottom: 4,
}

const auditValue: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 600,
  fontSize: 13,
  color: brand.ink,
}
